/**
 * Converts a date string to local timezone format
 * @param data - The date string to convert
 * @returns Formatted date string in YYYY-MM-DD HH:mm:ss format, or empty string if invalid
 */
export function convertLocalTimezone(data: string): string {
  if (!data || isNaN(Date.parse(data))) return '';
  const date = new Date(data.endsWith('Z') ? data : data + 'Z');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
} 

export function getMaxDayOfMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export type GroupByType = "year" | "month" | "day" | "hour" | "daydiffyear";

/**
 * Formats time for tooltip display based on the groupBy type
 * @param time - The date to format
 * @param groupBy - The grouping type that determines the format
 * @returns Formatted time string
 */
export function formatTooltipTime(time: Date, groupBy: GroupByType): string {
  if (groupBy === "hour") {
    return time.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } else if (groupBy === "month") {
    return time.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }
  return time.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats X-axis tick labels for charts based on the groupBy type
 * @param value - The date value to format
 * @param groupBy - The grouping type that determines the format
 * @returns Formatted date string for X-axis display
 */
export function formatXAxisTick(value: Date, groupBy: GroupByType): string {
  if (groupBy === "hour") {
    return value.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } else if (groupBy === "daydiffyear") {
    return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  }
  return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
}